import { AuthService } from './../../services/auth.service';
import { Component, EventEmitter, Input, Output, OnInit, ViewChild, forwardRef } from '@angular/core';
import { ClientService } from '../../services/client/client.service';
import { ClientDTO, PagedResultDTOOfClientDTO } from '../../models/models.swagger.generated';
import { MultiSelectItem } from '../autocomplete/multi-select-item';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { ControlValueAccessor, UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, AbstractControl } from '@angular/forms';
import { CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { TooltipModule } from '@syncfusion/ej2-angular-popups';
import { TranslateModule } from '@ngx-translate/core';


@Component({
    selector: 'app-client-autocomplete',
    templateUrl: './client-autocomplete.component.html',
    styleUrls: ['./client-autocomplete.component.scss'],
    imports: [AutocompleteComponent, CheckBoxModule, TooltipModule, TranslateModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ClientAutocompleteComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ClientAutocompleteComponent),
            multi: true
        }
    ]
})

export class ClientAutocompleteComponent implements ControlValueAccessor, OnInit {
  @ViewChild(AutocompleteComponent)
  public autoCompleteComponent: AutocompleteComponent;

  @Output() public selectedClientChange: EventEmitter<ClientDTO> = new EventEmitter<ClientDTO>();

  @Input() public placeholder = 'i18n.shared.search-company';

  @Input() public showHasImportConfigurationFilter: boolean = false;

  public hasImportConfigurationFilter: boolean = false;

  @Input() public showHasExportConfigurationFilter: boolean = false;

  public hasExportConfigurationFilter: boolean = false;

  @Input() public set selectedClient(client: ClientDTO) {
    if (this._selectedClient != client) {
      this._selectedClient = client;
      if (client && !this.clientsCache.find(c => c.id === client.id)) {
        this.clientsCache.push(client);
        this.autoCompleteComponent?.refresh();
      }
    }
  }

  public get isFormComponent(): boolean {
    return this._control != null;
  }

  public get selectedClientId(): number {
    if (this._selectedClient === null) {
      return null;
    }
    return this._selectedClient?.id;
  }

  public set selectedClientId(value: number) {
    if (this.selectedClientId != value) {
      const client = this.getClientById(value);
      this.selectedClientChange.emit(client);
      this._selectedClient = client;
      this.onChange(client);
      this.onTouched();
    }
  }

  public get isClientAdminView(): boolean {
    return this.authService.isUserClientUser();
  }

  public fields: Object = { text: 'name', value: 'id' };
  public selectedClientName: string;
  public isDisabled = false;

  private _selectedClient: ClientDTO;
  private clientsCache: ClientDTO[] = [];
  private _control: AbstractControl;

  constructor(
    private clientService: ClientService,
    private authService: AuthService,
  ) {
  }

  public onChange: any = () => {};
  public onTouched: any = () => {};

  public writeValue(client: ClientDTO): void {
    this.selectedClient = client;
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  public validate(control: UntypedFormControl) {
    this._control = control;
    return null;
  }

  public onHasImportConfigurationChange(checked: boolean): void {
    this.hasImportConfigurationFilter = checked;
    this.autoCompleteComponent?.refresh();
  }

  public onHasExportConfigurationChange(checked: boolean): void {
    this.hasExportConfigurationFilter = checked;
    this.autoCompleteComponent?.refresh();
  }

  ngOnInit(): void {
    if(this.authService.isUserClientUser()){
      this.selectedClientName = this.getClientsDisplayName(this.authService.getUserInfo().client);
    }
  }

  public searchClientData(searchText: string): Observable<MultiSelectItem[]> {
    const hasImportConfiguration = this.hasImportConfigurationFilter ? true : null;
    const hasExportConfiguration = this.hasExportConfigurationFilter ? true : null;
    return this.clientService.searchClients(searchText, 20, hasImportConfiguration, hasExportConfiguration).pipe(map((clientPaged: PagedResultDTOOfClientDTO) => {
      this.clientsCache = clientPaged.items;
      const clientId = this._selectedClient?.id;
      if (clientId && !this.clientsCache.find(c => c.id === clientId)) {
        this.clientsCache.push(this._selectedClient);
      }
      return clientPaged.items.map((client: ClientDTO) => {
        return { id: client.id, name: this.getClientsDisplayName(client) };
      });
    }));
  }

  private getClientById(id: number) {
    if (!id) {
      return null;
    }
    return this.clientsCache.find(c => c.id == id);
  }

  private getClientsDisplayName(client: ClientDTO) {
    if (!client) {
      return null;
    }
    return client.name + " (" + (client.customerNumber ?? client.vendorNumber) + ")";
  }
}
